import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  RefreshControl,
  Dimensions,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getEventAttendanceAnalytics } from '../services/database';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const MAX_BAR_WIDTH = SCREEN_WIDTH - 120;

const AttendanceAnalyticsScreen = ({ navigation, route }) => {
  const clubId = route.params?.clubId;
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    const id = clubId;
    if (!id) {
      setAnalytics(null);
      return;
    }
    try {
      const data = await getEventAttendanceAnalytics(id);
      setAnalytics(data);
    } catch (e) {
      console.error('Attendance analytics:', e);
      setAnalytics(null);
    }
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  if (!clubId) {
    return (
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <Text style={styles.errorText}>Club not specified</Text>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Text style={styles.backBtnText}>Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.headerBack}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Icon name="arrow-back" size={24} color="#333" />
          <Text style={styles.headerBackLabel}>Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Attendance Analytics</Text>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {loading && !analytics ? (
          <Text style={styles.loadingText}>Loading…</Text>
        ) : !analytics ? (
          <Text style={styles.errorText}>Failed to load analytics</Text>
        ) : (
          <>
            <View style={styles.cardsRow}>
              <View style={styles.card}>
                <Text style={styles.cardValue}>{analytics.totalAttendees ?? 0}</Text>
                <Text style={styles.cardLabel}>Total check-ins</Text>
              </View>
              <View style={styles.card}>
                <Text style={styles.cardValue}>{analytics.repeatAttendeeCount ?? 0}</Text>
                <Text style={styles.cardLabel}>Repeat attendees</Text>
              </View>
            </View>
            <View style={styles.cardsRow}>
              <View style={styles.card}>
                <Text style={styles.cardValue}>
                  {analytics.events?.reduce((sum, e) => sum + (e.no_show_count || 0), 0) ?? 0}
                </Text>
                <Text style={styles.cardLabel}>No-shows (total)</Text>
              </View>
              <View style={styles.card}>
                <Text style={styles.cardValue}>{analytics.events?.length ?? 0}</Text>
                <Text style={styles.cardLabel}>Events</Text>
              </View>
            </View>

            <Text style={styles.sectionTitle}>Attendance by event</Text>
            {(!analytics.events || analytics.events.length === 0) ? (
              <Text style={styles.emptyText}>No events with check-in data yet.</Text>
            ) : (
              analytics.events.map((ev) => {
                const rsvpCount = ev.rsvp_count || 0;
                const checkInCount = ev.check_in_count || 0;
                const noShow = ev.no_show_count || 0;
                const rate = rsvpCount > 0 ? Math.round((checkInCount / rsvpCount) * 100) : 0;
                const barScale = Math.max(0, ...analytics.events.map((e) => e.check_in_count || 0)) || 1;
                const barWidth = Math.max(0, (checkInCount / barScale) * MAX_BAR_WIDTH);
                const dateStr = ev.event_date
                  ? new Date(ev.event_date).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : '';
                return (
                  <View key={ev.id} style={styles.eventRow}>
                    <View style={styles.eventRowTop}>
                      <Text style={styles.eventTitle} numberOfLines={1}>
                        {ev.title}
                      </Text>
                      <Text style={styles.eventDate}>{dateStr}</Text>
                    </View>
                    <View style={styles.barRow}>
                      <View style={[styles.bar, { width: barWidth }]} />
                      <Text style={styles.barLabel}>{checkInCount} checked in</Text>
                    </View>
                    <View style={styles.eventMeta}>
                      <Text style={styles.eventMetaText}>
                        RSVPs: {rsvpCount} · No-shows: {noShow} · Check-in rate: {rate}%
                      </Text>
                    </View>
                  </View>
                );
              })
            )}

            <Text style={styles.sectionTitle}>Trend</Text>
            <Text style={styles.trendHint}>
              Check-ins over your recent events (left to right = oldest to newest).
            </Text>
            {analytics.events && analytics.events.length > 0 && (
              <View style={styles.trendBarContainer}>
                {analytics.events.slice().reverse().map((ev, idx) => {
                  const maxC = Math.max(1, ...analytics.events.map((e) => e.check_in_count || 0));
                  const h = Math.max(4, ((ev.check_in_count || 0) / maxC) * 120);
                  return (
                    <View key={ev.id} style={styles.trendBarWrap}>
                      <View style={[styles.trendBar, { height: h }]} />
                      <Text style={styles.trendBarLabel}>{ev.check_in_count || 0}</Text>
                    </View>
                  );
                })}
              </View>
            )}

            <View style={{ height: 80 }} />
          </>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  headerBack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerBackLabel: {
    fontSize: 16,
    color: '#333',
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginLeft: 16,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginTop: 24,
  },
  errorText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginTop: 24,
  },
  backBtn: {
    marginTop: 16,
    alignSelf: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#eee',
    borderRadius: 8,
  },
  backBtnText: {
    fontSize: 16,
    color: '#333',
  },
  cardsRow: {
    flexDirection: 'row',
    marginBottom: 12,
    gap: 12,
  },
  card: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    padding: 16,
  },
  cardValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#333',
  },
  cardLabel: {
    fontSize: 13,
    color: '#666',
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 24,
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 15,
    color: '#999',
    marginBottom: 24,
  },
  eventRow: {
    backgroundColor: '#f9f9f9',
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
  },
  eventRowTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  eventTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginRight: 8,
  },
  eventDate: {
    fontSize: 13,
    color: '#666',
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  bar: {
    height: 8,
    backgroundColor: '#4CAF50',
    borderRadius: 4,
    marginRight: 10,
  },
  barLabel: {
    fontSize: 13,
    color: '#666',
  },
  eventMeta: {
    marginTop: 4,
  },
  eventMetaText: {
    fontSize: 12,
    color: '#888',
  },
  trendHint: {
    fontSize: 13,
    color: '#666',
    marginBottom: 12,
  },
  trendBarContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 140,
    gap: 6,
  },
  trendBarWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  trendBar: {
    width: '100%',
    minWidth: 8,
    backgroundColor: '#2196F3',
    borderRadius: 4,
  },
  trendBarLabel: {
    fontSize: 10,
    color: '#666',
    marginTop: 4,
  },
});

export default AttendanceAnalyticsScreen;
